import { TECHNOLOGIES } from "@constants/global.constants";

import { AdjustPhIcon, AdjustWqaIcon, DischargeIcon, MixPointIcon, SplitIcon } from "@components/SVGs";
import { TREATMENT_IDS } from "@components/WPTechnologyIcons/WPTechnologyIcons.constants";

import { Position } from "@xyflow/react";

export const EDGE_INTERACTION_WIDTH = 50;

export const MAX_MLD_TREATMENT_NODES = 6;
export const MAX_MULTI_TECH_TREATMENT_NODES = 4;

export const TREATMENT_LIMITS = {
  [TECHNOLOGIES.UF]: 2,
  [TECHNOLOGIES.RO]: 4,
  [TECHNOLOGIES.IX]: 2,
};

export const MULTI_TECH_TREATMENT_LIMITS = {
  [TECHNOLOGIES.UF]: 1,
  [TECHNOLOGIES.RO]: 1,
  [TECHNOLOGIES.IX]: 2,
};

export const NODE_ORIGIN = [0.5, 0.5];

export const DEFAULT_ATMOSPHERIC_PRESSURE = 0;
export const DEFAULT_CONCENTRATE_PRESSURE = 0;

export const DEFAULT_SPLIT_STREAM_PERCENT = 50;

export const DEFAULT_SYSTEM_FLOW = 100;

export const NODE_TYPES = {
  FEED: "feed",
  TREATMENT: "treatment",
  ADJUSTMENT: "adjustment",
  DISCHARGE: "discharge",
  SPLIT: "split",
  MIX: "mix",
  PRODUCT: "product",
};

export const ADJUSTMENT_TYPES = {
  PH: "pH",
  WQA: "WQA",
};

export const HANDLE_TYPES = {
  SOURCE: "source",
  TARGET: "target",
};

export const EDGE_TYPES = {
  MAIN: "main",
  DISCHARGE: "discharge",
};

/**
 * Handle IDs - CRITICAL for multi-handle nodes
 * React Flow requires unique IDs when a node has multiple handles of the same type
 *
 * Naming Convention:
 * - MAIN_IN/MAIN_OUT: Main process flow (horizontal left → right)
 * - SPLIT_RIGHT/TOP/BOTTOM: Split outputs (position-based naming)
 * - MIX_LEFT/TOP/BOTTOM: Mix inputs (position-based naming)
 * - DISCHARGE_IN/DISCHARGE_OUT: Discharge stream handles (Treatment bottom → Discharge top/bottom)
 */
export const HANDLE_IDS = {
  // Main process flow handles
  MAIN_IN: "main-in",
  MAIN_OUT: "main-out",

  // Report-only RO -> UF overlay handles (top-to-top).
  // These are intentionally not part of NODE_HANDLES to avoid affecting live design editing.
  RO_TOP_PERMEATE_OUT: "ro-top-permeate-out",
  UF_TOP_PERMEATE_IN: "uf-top-permeate-in",

  // Split handles - position-based naming
  SPLIT_RIGHT: "split-right",
  SPLIT_TOP: "split-top",
  SPLIT_BOTTOM: "split-bottom",

  // Mix handles - position-based naming
  MIX_LEFT: "mix-left",
  MIX_TOP: "mix-top",
  MIX_BOTTOM: "mix-bottom",

  // Discharge stream handles
  DISCHARGE_IN: "discharge-in", // Discharge node inlet (top)
  DISCHARGE_OUT: "discharge-out", // Treatment node discharge port (bottom) → must connect to DISCHARGE_IN  OR Discharge node outlet (bottom)
};

/**
 * Node handle configuration - defines all valid connection points for each node type
 * Each handle MUST have a unique ID within its node for React Flow to function correctly
 * All handles in this domain support exactly one connection (1:1 relationships)
 *
 * Flow Direction Rules:
 * - Main flow: Always left → right (horizontal)
 * - Discharge flow: Always top → bottom (vertical, from treatment nodes only)
 *
 * Node-specific constraints enforced by validation layer:
 * - Feed: Source only (1 output)
 * - Product: Sink only (1 input)
 * - Treatment: Has discharge output that MUST connect to a discharge node
 * - Adjustment: No discharge output allowed
 * - Discharge: Terminal node for discharge streams
 * - Split: 1 input → 2 outputs
 * - Mix: 2 inputs → 1 output
 */
export const NODE_HANDLES = {
  [NODE_TYPES.FEED]: [
    {
      id: HANDLE_IDS.MAIN_OUT,
      handleType: HANDLE_TYPES.SOURCE,
      position: Position.Right,
    },
  ],
  [NODE_TYPES.TREATMENT]: [
    {
      id: HANDLE_IDS.MAIN_IN,
      handleType: HANDLE_TYPES.TARGET,
      position: Position.Left,
    },
    {
      id: HANDLE_IDS.MAIN_OUT,
      handleType: HANDLE_TYPES.SOURCE,
      position: Position.Right,
    },
    {
      id: HANDLE_IDS.DISCHARGE_OUT,
      handleType: HANDLE_TYPES.SOURCE,
      position: Position.Bottom,
    },
  ],
  [NODE_TYPES.ADJUSTMENT]: [
    {
      id: HANDLE_IDS.MAIN_IN,
      handleType: HANDLE_TYPES.TARGET,
      position: Position.Left,
    },
    {
      id: HANDLE_IDS.MAIN_OUT,
      handleType: HANDLE_TYPES.SOURCE,
      position: Position.Right,
    },
  ],
  [NODE_TYPES.DISCHARGE]: [
    {
      id: HANDLE_IDS.DISCHARGE_IN,
      handleType: HANDLE_TYPES.TARGET,
      position: Position.Top,
    },
    {
      id: HANDLE_IDS.DISCHARGE_OUT,
      handleType: HANDLE_TYPES.SOURCE,
      position: Position.Bottom,
    },
  ],
  [NODE_TYPES.SPLIT]: [
    {
      id: HANDLE_IDS.MAIN_IN,
      handleType: HANDLE_TYPES.TARGET,
      position: Position.Left,
    },
    {
      id: HANDLE_IDS.SPLIT_RIGHT,
      handleType: HANDLE_TYPES.SOURCE,
      position: Position.Right,
    },
    {
      id: HANDLE_IDS.SPLIT_TOP,
      handleType: HANDLE_TYPES.SOURCE,
      position: Position.Top,
    },
    {
      id: HANDLE_IDS.SPLIT_BOTTOM,
      handleType: HANDLE_TYPES.SOURCE,
      position: Position.Bottom,
    },
  ],
  [NODE_TYPES.MIX]: [
    {
      id: HANDLE_IDS.MIX_LEFT,
      handleType: HANDLE_TYPES.TARGET,
      position: Position.Left,
    },
    {
      id: HANDLE_IDS.MIX_BOTTOM,
      handleType: HANDLE_TYPES.TARGET,
      position: Position.Bottom,
    },
    {
      id: HANDLE_IDS.MIX_TOP,
      handleType: HANDLE_TYPES.TARGET,
      position: Position.Top,
    },
    {
      id: HANDLE_IDS.MAIN_OUT,
      handleType: HANDLE_TYPES.SOURCE,
      position: Position.Right,
    },
  ],
  [NODE_TYPES.PRODUCT]: [
    {
      id: HANDLE_IDS.MAIN_IN,
      handleType: HANDLE_TYPES.TARGET,
      position: Position.Left,
    },
  ],
};

const MIX_INPUT_HANDLE_IDS = NODE_HANDLES[NODE_TYPES.MIX]
  .filter(handle => handle.handleType === HANDLE_TYPES.TARGET)
  .map(handle => handle.id);

export const MIX_INPUT_HANDLE_SET = new Set(MIX_INPUT_HANDLE_IDS);

export const NODE_ICON_MAP = {
  [NODE_TYPES.SPLIT]: SplitIcon,
  [NODE_TYPES.MIX]: MixPointIcon,
  [NODE_TYPES.DISCHARGE]: DischargeIcon,
  [ADJUSTMENT_TYPES.PH]: AdjustPhIcon,
  [ADJUSTMENT_TYPES.WQA]: AdjustWqaIcon,
};

export const EDGE_COLOR_VARIANTS = {
  FEED: "feed",
  UF: "uf",
  UF_DISCHARGE: "uf-discharge",
  RO: "ro",
  RO_DISCHARGE: "ro-discharge",
  IX: "ix",
  IX_DISCHARGE: "ix-discharge",
  MIX: "mix",
};

export const EDGE_STATES = {
  DEFAULT: "default",
  HOVER: "hover",
  SELECTED: "selected",
};

export const EDGE_COLORS = {
  [EDGE_COLOR_VARIANTS.FEED]: {
    [EDGE_STATES.DEFAULT]: "var(--du-color-primary)",
    [EDGE_STATES.HOVER]: "var(--du-color-primary-teal-400)",
    [EDGE_STATES.SELECTED]: "var(--du-color-light-primary-aquamarine)",
  },
  [EDGE_COLOR_VARIANTS.UF]: {
    [EDGE_STATES.DEFAULT]: "var(--du-color-green-light)",
    [EDGE_STATES.HOVER]: "var(--du-color-green)",
    [EDGE_STATES.SELECTED]: "var(--du-color-green)",
  },
  [EDGE_COLOR_VARIANTS.UF_DISCHARGE]: {
    [EDGE_STATES.DEFAULT]: "var(--du-color-green)",
    [EDGE_STATES.HOVER]: "var(--du-color-green-light)",
    [EDGE_STATES.SELECTED]: "var(--du-color-green)",
  },
  [EDGE_COLOR_VARIANTS.RO]: {
    [EDGE_STATES.DEFAULT]: "var(--du-color-purple-light)",
    [EDGE_STATES.HOVER]: "var(--du-color-purple-primary)",
    [EDGE_STATES.SELECTED]: "var(--du-color-purple-primary)",
  },
  [EDGE_COLOR_VARIANTS.RO_DISCHARGE]: {
    [EDGE_STATES.DEFAULT]: "var(--du-color-purple-primary)",
    [EDGE_STATES.HOVER]: "var(--du-color-purple-light)",
    [EDGE_STATES.SELECTED]: "var(--du-color-purple-primary)",
  },
  [EDGE_COLOR_VARIANTS.IX]: {
    [EDGE_STATES.DEFAULT]: "var(--du-color-secondary-blue-light)",
    [EDGE_STATES.HOVER]: "var(--du-color-secondary-blue)",
    [EDGE_STATES.SELECTED]: "var(--du-color-secondary-blue)",
  },
  [EDGE_COLOR_VARIANTS.IX_DISCHARGE]: {
    [EDGE_STATES.DEFAULT]: "var(--du-color-secondary-blue)",
    [EDGE_STATES.HOVER]: "var(--du-color-secondary-blue-light)",
    [EDGE_STATES.SELECTED]: "var(--du-color-secondary-blue)",
  },
  [EDGE_COLOR_VARIANTS.MIX]: {
    [EDGE_STATES.DEFAULT]: "var(--du-color-orange-light)",
    [EDGE_STATES.HOVER]: "var(--du-color-orange)",
    [EDGE_STATES.SELECTED]: "var(--du-color-orange-dark)",
  },
};

export const EDGE_INSERTABLE_NODE_TYPES = [
  NODE_TYPES.TREATMENT,
  NODE_TYPES.ADJUSTMENT,
  NODE_TYPES.SPLIT,
  NODE_TYPES.MIX,
];

export const NEW_NODE_CONFIG_MAP = {
  [TREATMENT_IDS.FEED]: { type: NODE_TYPES.FEED, data: { flow: DEFAULT_SYSTEM_FLOW } },
  [TREATMENT_IDS.PRODUCT]: { type: NODE_TYPES.PRODUCT, data: { flow: DEFAULT_SYSTEM_FLOW } },
  [TREATMENT_IDS.SPLIT]: {
    type: NODE_TYPES.SPLIT,
    data: { splitStream1: DEFAULT_SPLIT_STREAM_PERCENT, splitStream2: DEFAULT_SPLIT_STREAM_PERCENT },
  },
  [TREATMENT_IDS.MIX]: { type: NODE_TYPES.MIX, data: {} },
  [TREATMENT_IDS.ADJUST_PH]: { type: NODE_TYPES.ADJUSTMENT, data: { adjustmentType: ADJUSTMENT_TYPES.PH } },
  [TREATMENT_IDS.WQA]: { type: NODE_TYPES.ADJUSTMENT, data: { adjustmentType: ADJUSTMENT_TYPES.WQA } },
};

export const NEW_NODE_DEFAULT_NAME_KEY_MAP = {
  [NODE_TYPES.FEED]: "systemDesign.feed",
  [NODE_TYPES.PRODUCT]: "systemDesign.product",
  [NODE_TYPES.SPLIT]: "systemDesign.split",
  [NODE_TYPES.MIX]: "systemDesign.mix",
  [ADJUSTMENT_TYPES.PH]: "systemDesign.finalPhAdj",
  [ADJUSTMENT_TYPES.WQA]: "systemDesign.wqaAdj",
};

export const RO_CONCENTRATE_PRESSURE_TYPES = {
  ATMOSPHERIC: "atmospheric",
  CONCENTRATE: "concentrate",
  SPECIFY: "specify",
};
export const RO_CONCENTRATE_PRESSURE_VALUES = {
  ATMOSPHERIC: 0,
  CONCENTRATE: 1,
  SPECIFY: 2,
};

export const DESIGN_FLOW_TYPES = {
  FEED: "Feed",
  PRODUCT: "Product",
};

const SPACING = 400;

export const INITIAL_FEED_NODE_ID = "feed-1";
export const INITIAL_PRODUCT_NODE_ID = "product-1";

export const getInitialNodes = t => [
  {
    id: INITIAL_FEED_NODE_ID,
    data: { name: t("systemDesign.feed") },
    position: { x: 0, y: 0 },
    type: NODE_TYPES.FEED,
  },
  {
    id: INITIAL_PRODUCT_NODE_ID,
    data: { name: t("systemDesign.product") },
    position: { x: SPACING * 2, y: 0 },
    type: NODE_TYPES.PRODUCT,
  },
];

export const INITIAL_EDGES = [];

export const DISCHARGE_PRESSURE_LEVELS = {
  HIGH: "High",
  LOW: "Low",
  HIGH_AND_LOW: "HighAndLow",
};
